/**
 * EventBus - Sistema de eventos síncronos para Realtime Perceptivo
 * 
 * PRINCÍPIO: Realtime nasce da INTENÇÃO do usuário, não do banco.
 * O EventBus emite eventos IMEDIATAMENTE após ação do usuário.
 * O banco é CONFIRMAÇÃO, não gatilho de UI.
 * 
 * IMPORTANTE:
 * - EventBus NÃO é fonte de verdade
 * - Fonte de verdade = React Query cache reconciliado com Supabase
 * - Eventos são para EFEITOS VISUAIS e RECONCILIAÇÃO
 */

// ============= TIPOS DE EVENTOS =============

export type IntentEventType =
  // Pedidos
  | 'pedido:criando'
  | 'pedido:criado'
  | 'pedido:status_atualizado'
  | 'pedido:editado'
  | 'pedido:removido'
  // Mensagens/Chat
  | 'mensagem:enviando'
  | 'mensagem:enviada'
  | 'mensagem:recebida'
  // Fiados
  | 'fiado:criado'
  | 'fiado:pago'
  // Caixa
  | 'caixa:atualizado'
  // Alertas
  | 'alerta:critico'
  // Confirmação do banco (reconciliação)
  | 'db:confirmado'
  | 'db:erro';

export interface EventPayload {
  // Pedidos
  'pedido:criando': { tempId: number; descricao: string; valor: number; clienteNome?: string };
  'pedido:criado': { tempId: number; pedido: unknown };
  'pedido:status_atualizado': { id: number; oldStatus: string; newStatus: string };
  'pedido:editado': { id: number; changes: Record<string, unknown> };
  'pedido:removido': { id: number };
  // Mensagens
  'mensagem:enviando': { tempId: number; conversa_id: string; mensagem: string };
  'mensagem:enviada': { tempId: number; mensagem: unknown };
  'mensagem:recebida': { mensagem: unknown };
  // Fiados
  'fiado:criado': { tempId: number; fiado: unknown };
  'fiado:pago': { id: number; valor: number };
  // Caixa
  'caixa:atualizado': { tipo: string; valor?: number };
  // Alertas
  'alerta:critico': { tipo: string; mensagem?: string; pedidoId?: number };
  // Confirmação/Erro do banco
  'db:confirmado': { 
    type: 'pedido' | 'mensagem' | 'fiado'; 
    tempId: number; 
    realId: number;
    eventId?: string;
  };
  'db:erro': { 
    type: 'pedido' | 'mensagem' | 'fiado'; 
    tempId: number; 
    error: string;
    eventId?: string;
  };
}

type EventHandler<T extends IntentEventType> = (payload: EventPayload[T]) => void;

// ============= IMPLEMENTAÇÃO SINGLETON =============

class EventBusImpl {
  private handlers = new Map<IntentEventType, Set<EventHandler<IntentEventType>>>();
  private debugMode = false;

  /**
   * Emite evento SÍNCRONO para todos os handlers registrados
   * Execução < 1ms (sem async, sem await)
   */
  emit<T extends IntentEventType>(event: T, payload: EventPayload[T]): void {
    if (this.debugMode) {
      console.log(`[EventBus] 📤 ${event}`, payload);
    }

    const eventHandlers = this.handlers.get(event);
    if (!eventHandlers || eventHandlers.size === 0) {
      return;
    }

    // Execução síncrona para todos os handlers
    eventHandlers.forEach(handler => {
      try {
        (handler as EventHandler<T>)(payload);
      } catch (err) {
        console.error(`[EventBus] Erro no handler de ${event}:`, err);
      }
    });
  }

  /**
   * Registra handler para um tipo de evento
   * Retorna função de cleanup
   */
  on<T extends IntentEventType>(event: T, handler: EventHandler<T>): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }

    this.handlers.get(event)!.add(handler as EventHandler<IntentEventType>);

    // Retorna função de cleanup
    return () => {
      this.off(event, handler);
    };
  }

  /**
   * Remove handler específico
   */
  off<T extends IntentEventType>(event: T, handler: EventHandler<T>): void {
    const eventHandlers = this.handlers.get(event);
    if (eventHandlers) {
      eventHandlers.delete(handler as EventHandler<IntentEventType>);
    }
  }

  /**
   * Registra handler que executa apenas uma vez
   */
  once<T extends IntentEventType>(event: T, handler: EventHandler<T>): () => void {
    const wrappedHandler: EventHandler<T> = (payload) => {
      this.off(event, wrappedHandler);
      handler(payload);
    };
    return this.on(event, wrappedHandler);
  }

  /**
   * Remove todos os handlers de um evento
   */
  clear(event?: IntentEventType): void {
    if (event) {
      this.handlers.delete(event);
    } else {
      this.handlers.clear();
    }
  }

  /**
   * Habilita/desabilita logs de debug
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }

  /**
   * Retorna quantidade de handlers por evento (para debug)
   */
  getHandlerCount(event: IntentEventType): number {
    return this.handlers.get(event)?.size || 0;
  }
}

// Singleton global
export const eventBus = new EventBusImpl();

// ============= HOOKS HELPERS =============

/**
 * Hook-friendly: registra handler e limpa automaticamente
 * Uso: useEventBusEffect('pedido:criado', (payload) => { ... }, [deps])
 */
export function createEventBusEffect<T extends IntentEventType>(
  event: T,
  handler: EventHandler<T>
): () => void {
  return eventBus.on(event, handler);
}

// ============= DEBUG =============

// Ativar debug em desenvolvimento
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as unknown as Record<string, unknown>).__eventBus = eventBus;
}
